import { useCallback, useEffect, useState } from 'react';
import { fetchSeasonsPlayed, fetchSeasonDivisions, SeasonDivisionCount } from '../../services/PlayerStatService';
import usePlayerStats from '../../hooks/usePlayerStats';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Loading from '../../components/Loading';
import { getRecentMatchesWithStats, getAllMatchesWithStats } from '../../services/MatchService';
import { parseMatchTime } from '../../utils/matchTime';
import { DivisionScope, getDivisionDisplayName, inDivisionScope, isKvalDivision } from '../../utils/divisionUtils';
import styles from './PlayerPage2.module.css';

// The stats block for the /player page. Only the season/side
// selectors, the two rating tiles (Rating 3.0 mode) and the "Seneste kampe" table
// are redesigned — everything else is identical to the old stats tab.

type Params = {
    steamId: string;
};

// Use the same Side type as SideSelector component
type Side = 'all' | 'overall' | 't' | 'ct';

const PlayerStats2: React.FC = () => {
    const { steamId } = useParams<Params>();
    const location = useLocation();
    const navigate = useNavigate();
    const [seasons, setSeasons] = useState<number[]>([]);
    const [season, setSeason] = useState<number | null>(null);
    const [selectedSide, setSelectedSide] = useState<Side>('overall');
    const [matchesWithStats, setMatchesWithStats] = useState<any[]>([]);
    const [matchesLoading, setMatchesLoading] = useState(false);
    const defaultMatchLimit = 12;
    const [matchLimit, setMatchLimit] = useState(defaultMatchLimit);
    const [isMatchHistoryExpanded, setIsMatchHistoryExpanded] = useState(false);
    const [filterSeason, setFilterSeason] = useState<string>('all');
    const [filterMap, setFilterMap] = useState<string>('all');
    const [filterTeam, setFilterTeam] = useState<string>('all');
    const [filterDivision, setFilterDivision] = useState<DivisionScope>('all');
    // Statistik's liga/kval split: only offered when the season has both (seasonDivisions).
    const [divisionScope, setDivisionScope] = useState<DivisionScope>('all');
    const [seasonDivisions, setSeasonDivisions] = useState<SeasonDivisionCount[]>([]);
    const apiScope = divisionScope === 'all' ? undefined : divisionScope;
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const { stats, loading } = usePlayerStats(steamId, season, apiScope);

    // Mobile detection
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Get unique values for filters from loaded matches
    const uniqueSeasons = Array.from(new Set(matchesWithStats.map(m => m.season))).sort((a, b) => b - a);
    const uniqueMaps = Array.from(new Set(matchesWithStats.map(m => m.mapName))).sort();
    const uniqueTeams = Array.from(new Set(matchesWithStats.flatMap(m => {
        const isTeam1 = m.team1playerids?.includes(steamId || '');
        return isTeam1 ? [m.team1?.teamName] : [m.team2?.teamName];
    }).filter(Boolean))).sort();

    // Filter matches based on selected filters
    const filteredMatches = matchesWithStats.filter(match => {
        const isTeam1 = match.team1playerids?.includes(steamId || '');
        const playerTeam = isTeam1 ? match.team1 : match.team2;

        if (filterSeason !== 'all' && match.season !== parseInt(filterSeason)) return false;
        if (filterMap !== 'all' && match.mapName !== filterMap) return false;
        if (filterTeam !== 'all' && playerTeam?.teamName !== filterTeam) return false;
        if (!inDivisionScope(match.division, filterDivision)) return false;
        return true;
    });

    // Kamphistorik only offers the Division filter when the loaded matches mix kval and liga.
    const historyHasKval = matchesWithStats.some(m => isKvalDivision(m.division ?? ''));
    const historyHasLiga = matchesWithStats.some(m => !isKvalDivision(m.division ?? ''));

    const fetchPlayedSeasons = useCallback(async () => {
        if (!steamId) return;
        try {
            const fetchedSeasons = await fetchSeasonsPlayed(steamId);
            const sortedSeasons = fetchedSeasons.sort((a, b) => b - a);
            setSeasons(sortedSeasons);

            // Check for season in URL parameters
            const params = new URLSearchParams(location.search);
            const seasonParam = params.get('season');
            if (seasonParam && fetchedSeasons.includes(Number(seasonParam))) {
                setSeason(Number(seasonParam));
            } else {
                setSeason(Math.max(...sortedSeasons));
            }
        } catch (error) {
            console.error(error);
        }
    }, [steamId, location.search]);

    const fetchMatchHistory = useCallback(async (selectedSeason: number, limit: number) => {
        if (!steamId) return;

        try {
            setMatchesLoading(true);
            // Use the new optimized endpoint that returns matches with stats in a single call
            const matchesData = await getRecentMatchesWithStats(steamId, selectedSeason, limit, apiScope);
            setMatchesWithStats(matchesData);
        } catch (error) {
            console.error('Error fetching match history:', error);
            setMatchesWithStats([]);
        } finally {
            setMatchesLoading(false);
        }
    }, [steamId, apiScope]);

    // Map name abbreviations
    const getMapAbbreviation = (mapName: string): string => {
        const abbreviations: { [key: string]: string } = {
            'de_ancient': 'anc',
            'de_anubis': 'anb',
            'de_dust2': 'd2',
            'de_inferno': 'inf',
            'de_mirage': 'mir',
            'de_nuke': 'nuk',
            'de_overpass': 'ovp',
            'de_vertigo': 'vtg',
            'de_train': 'trn',
            'de_cache': 'cch'
        };
        return abbreviations[mapName] || mapName.replace('de_', '').substring(0, 3);
    };

    useEffect(() => {
        if (!isMatchHistoryExpanded) {
            setMatchLimit(defaultMatchLimit);
        }
    }, [isMatchHistoryExpanded]);

    useEffect(() => {
        fetchPlayedSeasons();
    }, [fetchPlayedSeasons]);

    useEffect(() => {
        // Expanded Kamphistorik holds every season's matches; a scope change must not swap it
        // back to the short season list.
        if (season && !isMatchHistoryExpanded) {
            fetchMatchHistory(season, matchLimit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [season, matchLimit, fetchMatchHistory]);

    useEffect(() => {
        if (!steamId || !season) return;
        let cancelled = false;
        fetchSeasonDivisions(steamId, season)
            .then(d => { if (!cancelled) setSeasonDivisions(d); })
            .catch(() => { if (!cancelled) setSeasonDivisions([]); });
        return () => { cancelled = true; };
    }, [steamId, season]);

    const handleSeasonChange = (selectedSeason: number) => {
        setSeason(selectedSeason);
        setDivisionScope('all');
        setMatchLimit(defaultMatchLimit);
        setIsMatchHistoryExpanded(false);
    };

    const handleExpandMatchHistory = async () => {
        if (!steamId) return;
        setIsMatchHistoryExpanded(true);
        setMatchesLoading(true);
        try {
            const allMatches = await getAllMatchesWithStats(steamId, 500);
            setMatchesWithStats(allMatches);
        } catch (error) {
            console.error('Error fetching all matches:', error);
        } finally {
            setMatchesLoading(false);
        }
    };

    const handleCollapseMatchHistory = async () => {
        if (!steamId || !season) return;
        setIsMatchHistoryExpanded(false);
        setFilterSeason('all');
        setFilterMap('all');
        setFilterTeam('all');
        setFilterDivision('all');
        setMatchesLoading(true);
        try {
            const matchesData = await getRecentMatchesWithStats(steamId, season, 10, apiScope);
            setMatchesWithStats(matchesData);
        } catch (error) {
            console.error('Error fetching matches:', error);
        } finally {
            setMatchesLoading(false);
        }
    };

    const handleSideChange = (side: Side) => {
        setSelectedSide(side);
    };

    const handleMatchClick = (matchId: string) => {
        // Build return URL with current stats tab and season
        const returnUrl = `/player/${steamId}?tab=stat&season=${season}`;
        navigate(`/match/${matchId}?returnUrl=${encodeURIComponent(returnUrl)}`);
    };

    const getStatsForSide = () => {
        if (!stats) return null;

        if (selectedSide === 't') {
            return {
                gamesPlayed: stats.gamesPlayed,
                rating2: stats.t_Rating2 || 0,
                rating21: stats.t_Rating21 || 0,
                hs: stats.t_HS || 0,
                kd: stats.t_KD || 0,
                adr: stats.t_ADR || 0,
                kast: stats.t_KAST || 0,
                openingDuelWinPercentage: stats.t_openingDuelWinPercentage || 0,
                openingDuelAttemptPercentage: stats.t_openingDuelAttemptPercentage || 0,
                utilityDamage: stats.t_utilityDamage || 0,
                timeFlashed: stats.t_timeFlashed || 0,
                enemyFlashTime: stats.t_enemyFlashTime || 0,
                kills: stats.t_kills || 0,
                deaths: stats.t_deaths || 0,
                assists: stats.t_assists || 0,
                dmg: stats.t_DMG || 0,
                hsKills: stats.t_hsKills || 0,
                fk: stats.t_FK || 0,
                firstDeaths: stats.t_firstDeaths || 0,
                pistolKills: stats.t_pistolKills || 0,
                awpKills: stats.t_awpKills || 0,
                enemiesFlashed: stats.t_enemiesFlashed || 0,
                entryKillsT: stats.t_entryKillsT || 0,
                roundsPlayed: stats.t_roundsPlayed || 0,
                clutchAttempts: stats.clutchAttempts || 0,
                clutchWins: stats.clutchWins || 0
            };
        } else if (selectedSide === 'ct') {
            return {
                gamesPlayed: stats.gamesPlayed,
                rating2: stats.cT_Rating2 || 0,
                rating21: stats.cT_Rating21 || 0,
                hs: stats.cT_HS || 0,
                kd: stats.cT_KD || 0,
                adr: stats.cT_ADR || 0,
                kast: stats.cT_KAST || 0,
                openingDuelWinPercentage: stats.cT_openingDuelWinPercentage || 0,
                openingDuelAttemptPercentage: stats.cT_openingDuelAttemptPercentage || 0,
                utilityDamage: stats.cT_utilityDamage || 0,
                timeFlashed: stats.cT_timeFlashed || 0,
                enemyFlashTime: stats.cT_enemyFlashTime || 0,
                kills: stats.cT_kills || 0,
                deaths: stats.cT_deaths || 0,
                assists: stats.cT_assists || 0,
                dmg: stats.cT_DMG || 0,
                hsKills: stats.cT_hsKills || 0,
                fk: stats.cT_FK || 0,
                firstDeaths: stats.cT_firstDeaths || 0,
                pistolKills: stats.cT_pistolKills || 0,
                awpKills: stats.cT_awpKills || 0,
                enemiesFlashed: stats.cT_enemiesFlashed || 0,
                roundsPlayed: stats.cT_roundsPlayed || 0,
                clutchAttempts: stats.clutchAttempts || 0,
                clutchWins: stats.clutchWins || 0
            };
        }

        // Overall stats (default)
        return {
            ...stats,
            roundsPlayed: stats.roundsPlayed
        };
    };

    if (loading) {
        return (
            <Loading />
        );
    }

    if (!stats || !season) {
        return (
            <div className="container text-center">
                <h1>Ingen statistik fundet</h1>
            </div>
        )
    }

    // ===== Rating mode (§3) =====
    // r3: season >= 32 AND the aggregated Rating 3.0 for the selected side is non-null.
    // r21: seasons 29-31 (or >= 32 without R3 data). r2: legacy seasons < 29.
    // Null discipline: a null rating30/swing means "no R3 data" and must never render as 0.
    const sideRating30 = selectedSide === 't' ? stats.t_Rating30
        : selectedSide === 'ct' ? stats.cT_Rating30
        : stats.rating30;
    const sideSwingPct = selectedSide === 't' ? stats.t_SwingPct
        : selectedSide === 'ct' ? stats.cT_SwingPct
        : stats.swingPct;
    const sideTradedDeaths = selectedSide === 't' ? stats.t_TradedDeaths
        : selectedSide === 'ct' ? stats.cT_TradedDeaths
        : stats.tradedDeaths;
    const isR3 = season >= 32 && sideRating30 != null;
    const ratingLabel = isR3 ? '3.0' : season >= 29 ? '2.1' : '2.0';

    const formatSwing = (value: number | null | undefined): string =>
        value != null ? `${value >= 0 ? '+' : ''}${value.toFixed(2)}%` : '–';

    // Per-row rating for the new table: side-scoped rating30 when non-null, else rating21,
    // else rating2 — plus which one it was, so non-3.0 values get an asterisk.
    // The legacy columns store 0, not NULL, when a rating was never computed (rating21 and
    // both side ratings before S29), so a 0 there means "missing" and must fall through.
    const getRowRating = (match: any): { value: number; kind: 'r3' | 'r21' | 'r2' } | null => {
        const [r30, r21, r2] = selectedSide === 't' ? [match.tSideRating30, match.tRating21, match.tRating2]
            : selectedSide === 'ct' ? [match.ctSideRating30, match.ctRating21, match.ctRating2]
            : [match.rating30, match.rating21, match.rating2];
        if (r30 != null) return { value: r30, kind: 'r3' };
        if (r21) return { value: r21, kind: 'r21' };
        if (r2) return { value: r2, kind: 'r2' };
        return null;
    };

    // Tooltip text for a non-3.0 rating asterisk.
    const ratingFallbackTitle = (kind: 'r21' | 'r2'): string =>
        kind === 'r21'
            ? 'Rating 2.1 — Rating 3.0 er ikke tilgængelig for denne kamp'
            : 'Rating 2.0 — Rating 3.0 er ikke tilgængelig for denne kamp';

    const getRowKills = (match: any): number =>
        selectedSide === 't' ? match.tKills : selectedSide === 'ct' ? match.ctKills : match.kills;
    const getRowDeaths = (match: any): number =>
        selectedSide === 't' ? match.tDeaths : selectedSide === 'ct' ? match.ctDeaths : match.deaths;

    // ===== §2b liga/kval picker: a small menu beside the side toggle, only for a season
    // that has both qualifier and league maps. =====
    const kvalMaps = seasonDivisions.filter(d => isKvalDivision(d.division)).reduce((n, d) => n + d.maps, 0);
    const ligaDivisions = seasonDivisions.filter(d => !isKvalDivision(d.division));
    const ligaMaps = ligaDivisions.reduce((n, d) => n + d.maps, 0);
    const scopeOptions: { key: DivisionScope; label: string; maps: number }[] = [
        { key: 'all', label: 'Alle kampe', maps: kvalMaps + ligaMaps },
        { key: 'liga', label: ligaDivisions.length === 1 ? getDivisionDisplayName(ligaDivisions[0].division) : 'Liga', maps: ligaMaps },
        { key: 'kval', label: 'Kvalifikation', maps: kvalMaps },
    ];
    const currentScope = scopeOptions.find(o => o.key === divisionScope) ?? scopeOptions[0];

    const renderScopePicker = () => (kvalMaps > 0 && ligaMaps > 0) ? (
        <div className="dropdown">
            <button
                type="button"
                className={`${styles.scopeToggle} ${divisionScope !== 'all' ? styles.scopeToggleActive : ''}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
            >
                {currentScope.label}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            <ul className={`dropdown-menu dropdown-menu-end ${styles.scopeMenu}`}>
                {scopeOptions.map(o => (
                    <li key={o.key}>
                        <button
                            type="button"
                            className={`${styles.scopeItem} ${o.key === divisionScope ? styles.scopeItemActive : ''}`}
                            onClick={() => setDivisionScope(o.key)}
                        >
                            <span>{o.label}</span>
                            <span className={styles.scopeItemCount}>{o.maps} kampe</span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    ) : null;

    // ===== §2 header row: SÆSON pills (all seasons, desc) + optional side toggle =====
    const renderStatsHeaderRow = (withSideToggle: boolean) => (
        <div className={styles.statsHeaderRow}>
            <div className={styles.seasonGroup}>
                <span className={styles.seasonLabel}>SÆSON</span>
                <div className={styles.seasonPills}>
                    {seasons.map(s => (
                        <button
                            key={s}
                            type="button"
                            className={`${styles.seasonPill} ${s === season ? styles.seasonPillActive : ''}`}
                            onClick={() => handleSeasonChange(s)}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>
            <div className={styles.headerControls}>
            {renderScopePicker()}
            {withSideToggle && (
                <div className={styles.sideToggle}>
                    <button
                        type="button"
                        className={`${styles.sideBtn} ${selectedSide === 'overall' ? styles.sideBtnActiveAll : ''}`}
                        onClick={() => handleSideChange('overall')}
                    >
                        All
                    </button>
                    <button
                        type="button"
                        className={`${styles.sideBtn} ${selectedSide === 't' ? styles.sideBtnActiveT : ''}`}
                        onClick={() => handleSideChange('t')}
                    >
                        T
                    </button>
                    <button
                        type="button"
                        className={`${styles.sideBtn} ${selectedSide === 'ct' ? styles.sideBtnActiveCT : ''}`}
                        onClick={() => handleSideChange('ct')}
                    >
                        CT
                    </button>
                </div>
            )}
            </div>
        </div>
    );

    // ===== §4 the redesigned "Seneste kampe" table =====
    const renderMatchTable = () => {
        const rows = isMatchHistoryExpanded ? filteredMatches : matchesWithStats;

        // Gns. row (expanded only): averages of the displayed values. Null ratings are
        // skipped (never counted as 0).
        const rowRatings = rows.map(getRowRating);
        const ratedValues = rowRatings.filter(r => r != null).map(r => r!.value);
        const avgKills = rows.length > 0 ? rows.reduce((sum, m) => sum + getRowKills(m), 0) / rows.length : 0;
        const avgDeaths = rows.length > 0 ? rows.reduce((sum, m) => sum + getRowDeaths(m), 0) / rows.length : 0;
        const avgRating = ratedValues.length > 0
            ? ratedValues.reduce((sum, v) => sum + v, 0) / ratedValues.length
            : null;

        // Header names the best rating among the shown rows, not the page's season: expanded
        // Kamphistorik spans every season, so a S26 filter holds only Rating 2.0 rows.
        const kinds = new Set(rowRatings.map(r => r?.kind));
        const columnLabel = kinds.has('r3') ? '3.0' : kinds.has('r21') ? '2.1' : kinds.has('r2') ? '2.0' : ratingLabel;

        return (
            <>
                <div className={styles.matchTable}>
                    <div className={styles.matchHeader}>
                        <div>Dato</div>
                        <div>Mod</div>
                        <div>Map</div>
                        <div style={{ textAlign: 'center' }}>K-D</div>
                        <div style={{ textAlign: 'center' }}>{columnLabel}</div>
                    </div>
                    {rows.map((match, index) => {
                        // Find which team the player was on
                        const isTeam1 = match.team1playerids?.includes(steamId || '');
                        const opponentTeam = isTeam1 ? match.team2 : match.team1;
                        const playerScore = isTeam1 ? match.team1score : match.team2score;
                        const opponentScore = isTeam1 ? match.team2score : match.team1score;

                        // Format date
                        const matchDate = parseMatchTime(match.match_time);
                        const dateStr = `${matchDate.getDate().toString().padStart(2, '0')}/${(matchDate.getMonth() + 1).toString().padStart(2, '0')}`;

                        const isWin = playerScore > opponentScore;
                        const rowRating = rowRatings[index];

                        return (
                            <div
                                key={index}
                                className={styles.matchRow}
                                onClick={() => handleMatchClick(match.matchid)}
                            >
                                <div className={styles.cellDate}>{dateStr}</div>
                                <div className={styles.cellOpp}>
                                    {opponentTeam?.teamName || 'Unknown'}{' '}
                                    <span className={isWin ? styles.oppScoreWin : styles.oppScoreLoss}>
                                        ({playerScore}-{opponentScore})
                                    </span>
                                    {isKvalDivision(match.division ?? '') && (
                                        <span className={styles.kvalTag}>KVAL</span>
                                    )}
                                </div>
                                <div className={styles.cellMap}>{getMapAbbreviation(match.mapName)}</div>
                                <div className={styles.cellKD}>{`${getRowKills(match)}-${getRowDeaths(match)}`}</div>
                                <div className={styles.cellRating}>
                                    {rowRating != null ? (
                                        <>
                                            {rowRating.value.toFixed(2)}
                                            {rowRating.kind !== 'r3' && (
                                                <span className={styles.ratingFallback} title={ratingFallbackTitle(rowRating.kind)}>*</span>
                                            )}
                                        </>
                                    ) : '–'}
                                </div>
                            </div>
                        );
                    })}
                    {isMatchHistoryExpanded && rows.length > 0 && (
                        <div className={styles.matchFooter}>
                            <div className={styles.footerLabel}>Gns.</div>
                            <div className={styles.cellKD}>{avgKills.toFixed(1)}-{avgDeaths.toFixed(1)}</div>
                            <div className={styles.cellRating}>{avgRating != null ? avgRating.toFixed(2) : '–'}</div>
                        </div>
                    )}
                </div>
                {!isMatchHistoryExpanded && (
                    <button
                        type="button"
                        className={styles.historyBtn}
                        onClick={handleExpandMatchHistory}
                    >
                        Vis kamphistorik
                    </button>
                )}
            </>
        );
    };

    // Heading + collapse button + expanded filters — unchanged behavior from the old page
    const renderMatchHistoryTools = () => (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h3 className="mb-0" style={{ fontSize: "1.1rem", fontWeight: 600, color: "#ffffff" }}>
                    {isMatchHistoryExpanded ? "Kamphistorik" : "Seneste kampe"}
                </h3>
                {isMatchHistoryExpanded && (
                    <button
                        className="btn btn-outline-light btn-sm"
                        onClick={handleCollapseMatchHistory}
                    >
                        Vis mindre
                    </button>
                )}
            </div>
            {isMatchHistoryExpanded && (
                <div className="d-flex gap-2 flex-wrap">
                    <select
                        className="form-select form-select-sm"
                        style={{ flex: '1 1 100px', minWidth: '100px', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                        value={filterSeason}
                        onChange={(e) => setFilterSeason(e.target.value)}
                    >
                        <option value="all">Sæson</option>
                        {uniqueSeasons.map(s => (
                            <option key={s} value={s}>S{s}</option>
                        ))}
                    </select>
                    <select
                        className="form-select form-select-sm"
                        style={{ flex: '1 1 100px', minWidth: '100px', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                        value={filterMap}
                        onChange={(e) => setFilterMap(e.target.value)}
                    >
                        <option value="all">Map</option>
                        {uniqueMaps.map(m => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                    <select
                        className="form-select form-select-sm"
                        style={{ flex: '1 1 100px', minWidth: '100px', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
                        value={filterTeam}
                        onChange={(e) => setFilterTeam(e.target.value)}
                    >
                        <option value="all">Hold</option>
                        {uniqueTeams.map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                    {historyHasKval && historyHasLiga && (
                        <select
                            className="form-select form-select-sm"
                            style={{ flex: '1 1 100px', minWidth: '100px', backgroundColor: 'var(--bg-secondary)', color: filterDivision !== 'all' ? '#ff8a2a' : 'var(--text-primary)', border: `1px solid ${filterDivision !== 'all' ? '#f15c25' : 'var(--border-primary)'}` }}
                            value={filterDivision}
                            onChange={(e) => setFilterDivision(e.target.value as DivisionScope)}
                        >
                            <option value="all">Division</option>
                            <option value="liga">Kun liga</option>
                            <option value="kval">Kun kvalifikation</option>
                        </select>
                    )}
                </div>
            )}
        </div>
    );

    const renderMatchHistorySection = () => (
        matchesLoading ? (
            <div className="text-center">
                <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        ) : matchesWithStats.length > 0 ? (
            renderMatchTable()
        ) : (
            <p className="text-muted">No matches found</p>
        )
    );

    if (season >= 29) {
        const displayStats = getStatsForSide();
        if (!displayStats) return null;

        return (
        <div className="container">
            {renderStatsHeaderRow(true)}

            {!isR3 && (
                <div className={styles.ratingNote}>
                    Rating 3.0 findes kun fra sæson 32 — her vises Rating 2.1.
                </div>
            )}

            {/* Main Stats Cards - First Row (6 cards) */}
            <div className="row mb-3 g-2">
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.gamesPlayed}</div>
                        <div className="stat-card-label">KAMPE SPILLET</div>
                    </div>
                </div>
                {isR3 ? (
                    <>
                        <div className="col-lg-2 col-md-4 col-4 mb-2">
                            <div className="stat-card stat-card-compact">
                                <div className="stat-card-value">{sideRating30!.toFixed(2)}</div>
                                <div className="stat-card-label">RATING 3.0</div>
                            </div>
                        </div>
                        <div className="col-lg-2 col-md-4 col-4 mb-2">
                            <div className="stat-card stat-card-compact">
                                <div className="stat-card-value">{formatSwing(sideSwingPct)}</div>
                                <div className="stat-card-label">SWING</div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="col-lg-2 col-md-4 col-4 mb-2">
                            <div className="stat-card stat-card-compact">
                                <div className="stat-card-value">{displayStats.rating2}</div>
                                <div className="stat-card-label">RATING 2.0</div>
                            </div>
                        </div>
                        <div className="col-lg-2 col-md-4 col-4 mb-2">
                            <div className="stat-card stat-card-compact">
                                <div className="stat-card-value">{displayStats.rating21}</div>
                                <div className="stat-card-label">RATING 2.1</div>
                            </div>
                        </div>
                    </>
                )}
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.hs}%</div>
                        <div className="stat-card-label">HS%</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.kd}</div>
                        <div className="stat-card-label">K/D</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.adr}</div>
                        <div className="stat-card-label">ADR</div>
                    </div>
                </div>
            </div>

            {/* Main Stats Cards - Second Row (6 cards) */}
            <div className="row mb-3 g-2">
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.kast}%</div>
                        <div className="stat-card-label">KAST%</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.openingDuelWinPercentage}%</div>
                        <div className="stat-card-label">OP DUEL WIN%</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.openingDuelAttemptPercentage}%</div>
                        <div className="stat-card-label">OP DUEL ATT%</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.utilityDamage}</div>
                        <div className="stat-card-label">UTIL DMG</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.timeFlashed.toFixed(2)}</div>
                        <div className="stat-card-label">TIME FLASHED</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{displayStats.enemyFlashTime.toFixed(2)}</div>
                        <div className="stat-card-label">ENEMY FLASHED</div>
                    </div>
                </div>
            </div>
            <hr />
            <div className="row mt-4">
                {!isMatchHistoryExpanded && (
                <div className="col-md-6">
                    <div className="row">
                        <div className="col-md-6">
                            <h3 className="mb-3" style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff" }}>Total</h3>
                            <div className="stat-item">
                                <span className="stat-label">Kills</span>
                                <span className="stat-value">{displayStats.kills}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Deaths</span>
                                <span className="stat-value">{displayStats.deaths}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Assists</span>
                                <span className="stat-value">{displayStats.assists}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">HS kills</span>
                                <span className="stat-value">{displayStats.hsKills}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">First Kills</span>
                                <span className="stat-value">{displayStats.fk}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">First Deaths</span>
                                <span className="stat-value">{displayStats.firstDeaths}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Pistol Kills</span>
                                <span className="stat-value">{displayStats.pistolKills || 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">AWP Kills</span>
                                <span className="stat-value">{displayStats.awpKills || 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Enemies Flashed</span>
                                <span className="stat-value">{displayStats.enemiesFlashed}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Damage</span>
                                <span className="stat-value">{displayStats.dmg}</span>
                            </div>
                            {(
                                <>
                                    <div className="stat-item">
                                        <span className="stat-label">Traded Deaths</span>
                                        <span className="stat-value">{sideTradedDeaths != null ? sideTradedDeaths : '–'}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Clutch Attempts</span>
                                        <span className="stat-value">{displayStats.clutchAttempts}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Clutch Wins</span>
                                        <span className="stat-value">
                                            {displayStats.clutchWins}
                                            <span style={{opacity:0.5, marginLeft:4, fontSize:'0.85em'}}>
                                                ({displayStats.clutchAttempts > 0 ? ((displayStats.clutchWins / displayStats.clutchAttempts) * 100).toFixed(0) : 0}%)
                                            </span>
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="col-12 d-md-none my-3">
                            <div className="section-divider"></div>
                        </div>
                        <div className="col-md-6">
                            <h3 className="mb-3" style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff" }}>Per kamp</h3>
                            <div className="stat-item">
                                <span className="stat-label">Kills</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.kills / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Deaths</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.deaths / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Assists</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.assists / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">HS kills</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.hsKills / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">First Kills</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.fk / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">First Deaths</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.firstDeaths / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Pistol Kills</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? ((displayStats.pistolKills || 0) / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">AWP Kills</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? ((displayStats.awpKills || 0) / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Enemies Flashed</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.enemiesFlashed / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Damage</span>
                                <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.dmg / displayStats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            {(
                                <>
                                    <div className="stat-item">
                                        <span className="stat-label">Traded Deaths</span>
                                        <span className="stat-value">{sideTradedDeaths != null && displayStats.gamesPlayed > 0 ? (sideTradedDeaths / displayStats.gamesPlayed).toFixed(1) : '–'}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Clutch Attempts</span>
                                        <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.clutchAttempts / displayStats.gamesPlayed).toFixed(1) : 0}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-label">Clutch Wins</span>
                                        <span className="stat-value">{displayStats.gamesPlayed > 0 ? (displayStats.clutchWins / displayStats.gamesPlayed).toFixed(1) : 0}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                )}
                <div
                    className={isMatchHistoryExpanded ? "col-12" : "col-md-6 d-flex flex-column"}
                    style={isMatchHistoryExpanded && isMobile ? { marginLeft: '-12px', marginRight: '-12px', width: 'calc(100% + 24px)', maxWidth: 'none' } : {}}
                >
                    {!isMatchHistoryExpanded && (
                    <div className="d-md-none my-3">
                        <div className="section-divider"></div>
                    </div>
                    )}
                    {renderMatchHistoryTools()}
                    <div className="flex-grow-1 d-flex flex-column">
                        {renderMatchHistorySection()}
                    </div>
                </div>
            </div>
        </div>
    );
    }

    return (
        <div className="container">
            {renderStatsHeaderRow(false)}

            <div className={styles.ratingNote}>
                Ældre sæson — kun Rating 2.0 tilgængelig.
            </div>

            {/* Main Stats Cards - First Row (6 cards) */}
            <div className="row mb-3 g-2">
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.gamesPlayed}</div>
                        <div className="stat-card-label">KAMPE SPILLET</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.rating2}</div>
                        <div className="stat-card-label">RATING 2.0</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.kast}%</div>
                        <div className="stat-card-label">KAST%</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.hs}%</div>
                        <div className="stat-card-label">HS%</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.kd}</div>
                        <div className="stat-card-label">K/D</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.adr}</div>
                        <div className="stat-card-label">ADR</div>
                    </div>
                </div>
            </div>

            {/* Main Stats Cards - Second Row (6 cards for seasons < 29) */}
            <div className="row mb-3 g-2">
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.openingDuelWinPercentage}%</div>
                        <div className="stat-card-label">OP DUEL WIN%</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.openingDuelAttemptPercentage}%</div>
                        <div className="stat-card-label">OP DUEL ATT%</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.utilityDamage}</div>
                        <div className="stat-card-label">UTIL DMG</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.enemiesFlashed || 0}</div>
                        <div className="stat-card-label">ENEMIES FLASHED</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.timeFlashed.toFixed(2)}</div>
                        <div className="stat-card-label">TIME FLASHED</div>
                    </div>
                </div>
                <div className="col-lg-2 col-md-4 col-4 mb-2">
                    <div className="stat-card stat-card-compact">
                        <div className="stat-card-value">{stats.enemyFlashTime.toFixed(2)}</div>
                        <div className="stat-card-label">ENEMY FLASHED</div>
                    </div>
                </div>
            </div>
            <hr />
            <div className="row mt-4">
                {!isMatchHistoryExpanded && (
                <div className="col-md-6">
                    <div className="row">
                        <div className="col-md-6">
                            <h3 className="mb-3" style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff" }}>Total</h3>
                            <div className="stat-item">
                                <span className="stat-label">Kills</span>
                                <span className="stat-value">{stats.kills}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Deaths</span>
                                <span className="stat-value">{stats.deaths}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Assists</span>
                                <span className="stat-value">{stats.assists}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">HS kills</span>
                                <span className="stat-value">{stats.hsKills}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">First Kills</span>
                                <span className="stat-value">{stats.fk}</span>
                            </div>
                            {season >= 26 && (
                                <div className="stat-item">
                                    <span className="stat-label">AWP Kills</span>
                                    <span className="stat-value">{stats.awpKills || 0}</span>
                                </div>
                            )}
                            <div className="stat-item">
                                <span className="stat-label">Damage</span>
                                <span className="stat-value">{stats.dmg}</span>
                            </div>
                        </div>
                        <div className="col-12 d-md-none my-3">
                            <div className="section-divider"></div>
                        </div>
                        <div className="col-md-6">
                            <h3 className="mb-3" style={{ fontSize: "1.25rem", fontWeight: 600, color: "#ffffff" }}>Per kamp</h3>
                            <div className="stat-item">
                                <span className="stat-label">Kills</span>
                                <span className="stat-value">{stats.gamesPlayed > 0 ? (stats.kills / stats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Deaths</span>
                                <span className="stat-value">{stats.gamesPlayed > 0 ? (stats.deaths / stats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Assists</span>
                                <span className="stat-value">{stats.gamesPlayed > 0 ? (stats.assists / stats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">HS kills</span>
                                <span className="stat-value">{stats.gamesPlayed > 0 ? (stats.hsKills / stats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">First Kills</span>
                                <span className="stat-value">{stats.gamesPlayed > 0 ? (stats.fk / stats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                            {season >= 26 && (
                                <div className="stat-item">
                                    <span className="stat-label">AWP Kills</span>
                                    <span className="stat-value">{stats.gamesPlayed > 0 ? ((stats.awpKills || 0) / stats.gamesPlayed).toFixed(0) : 0}</span>
                                </div>
                            )}
                            <div className="stat-item">
                                <span className="stat-label">Damage</span>
                                <span className="stat-value">{stats.gamesPlayed > 0 ? (stats.dmg / stats.gamesPlayed).toFixed(0) : 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
                )}
                <div
                    className={isMatchHistoryExpanded ? "col-12" : "col-md-6"}
                    style={isMatchHistoryExpanded && isMobile ? { marginLeft: '-12px', marginRight: '-12px', width: 'calc(100% + 24px)', maxWidth: 'none' } : {}}
                >
                    {!isMatchHistoryExpanded && (
                    <div className="d-md-none my-3">
                        <div className="section-divider"></div>
                    </div>
                    )}
                    {renderMatchHistoryTools()}
                    {renderMatchHistorySection()}
                </div>
            </div>
        </div>
    );
};

export default PlayerStats2;
